import { useEffect, useMemo } from 'react'
import { useFontPairings } from 'hooks/queries/fontPairings'
import { useFonts } from 'hooks/queries/workspaces/useFonts'
import { useRootStore } from 'stores/RootStore'
import Styles from './VisualStylePreview.module.scss'
import { TextOverlay } from './TextOverlay'
import { StyleGenerated } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleSelection/GenerationStage'
import { InstagramPreview } from 'components/Previews/InstagramPreview/InstagramPreview'

export interface VisualStylePreviewProps {
  fontPairingId: number | null
  styleGenerated: StyleGenerated
  brandColor?: string
}

export const VisualStylePreview = ({ fontPairingId, brandColor, styleGenerated }: VisualStylePreviewProps) => {
  const { data: fontPairingsData } = useFontPairings()
  const { data: customFontsData } = useFonts()
  const { googleFonts, blazeFonts, customFonts } = useRootStore()
  const firstCustomFont = customFontsData?.fonts?.[0]

  // Preload all header fonts from font pairings to avoid render delay when switching
  useEffect(() => {
    if (!fontPairingsData) return

    const headerFontFamilies = fontPairingsData
      .map((fp) => fp.header_font_config?.family)
      .filter((family): family is string => !!family)

    // Deduplicate
    const uniqueFamilies = [...new Set(headerFontFamilies)]

    // Preload via Google Fonts
    if (googleFonts.ready) {
      uniqueFamilies.forEach((family) => googleFonts.load(family))
    }

    // Preload via Blaze Fonts (custom fonts)
    if (blazeFonts.ready) {
      uniqueFamilies.forEach((name) => blazeFonts.load(name))
    }
  }, [fontPairingsData, googleFonts, blazeFonts])

  // Load custom font if available
  useEffect(() => {
    if (firstCustomFont) {
      customFonts.load(firstCustomFont.name)
    }
  }, [firstCustomFont, customFonts])

  const selectedFontPairing = useMemo(
    () => fontPairingsData?.find((fp) => fp.id === fontPairingId),
    [fontPairingsData, fontPairingId],
  )

  // Build inline styles from header font config or custom font
  const headerFontStyle = useMemo(() => {
    // If user has custom fonts, use the first one
    if (firstCustomFont) {
      return {
        fontFamily: `"${firstCustomFont.font_family}", sans-serif`,
        fontWeight: 400,
        fontStyle: 'normal',
      }
    }

    // Otherwise use the selected font pairing
    const config = selectedFontPairing?.header_font_config
    if (!config) return {}

    return {
      fontFamily: `"${config.family}", sans-serif`,
      fontWeight: config.weight,
      fontStyle: config.italic ? 'italic' : 'normal',
      letterSpacing: `${config.letter_spacing / 1000}em`,
    }
  }, [firstCustomFont, selectedFontPairing?.header_font_config])

  const headerFontFamily = firstCustomFont?.font_family ?? selectedFontPairing?.header_font_config?.family
  const styledUrl = styleGenerated.styleUrl

  return (
    <div className={Styles.container}>
      <>
        <InstagramPreview
          content="The ultimate secret to mastering your personal brand is something that at..."
          maxWidth={367}
          isGenerating
          mediaObjects={[
            { thumbnail_url: styledUrl, source_url: styledUrl, id: 1, mime_type: 'image/png', position: 1 },
          ]}
          captionElement={
            headerFontFamily && (
              <TextOverlay
                key={headerFontFamily}
                fontStyle={headerFontStyle}
                variant="bottom-left"
                textColor={brandColor}
              />
            )
          }
        />
      </>
    </div>
  )
}
